import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, ClipboardList, Stethoscope } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { getStoredAccountType } from "../auth";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { loadStandardActCodes } from "../standardActCodesStorage";
import { loadStandardDiagnosticCodes } from "../standardDiagnosticCodesStorage";

type OverviewCardProps = {
  title: string;
  description: string;
  countLabel: string;
  linkLabel: string;
  to: string;
  icon: React.ReactNode;
};

function OverviewCard({ title, description, countLabel, linkLabel, to, icon }: OverviewCardProps) {
  return (
    <Link
      to={to}
      className="theme-card group flex flex-col gap-4 p-6 transition-colors hover:border-primary/40"
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </div>
      <div className="flex-1">
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{description}</p>
        <p className="mt-3 text-sm font-medium text-primary">{countLabel}</p>
      </div>
      <span className="inline-flex items-center gap-2 text-sm font-medium text-primary">
        {linkLabel}
        <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}

export function ActsOverviewPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.acts.overview;
  const accountType = getStoredAccountType();
  const homeHref =
    accountType === "auditor"
      ? "/dashboard/auditor"
      : accountType === "hospital"
        ? "/dashboard/hospital"
        : "/dashboard/admin";

  const [actsCount, setActsCount] = useState(0);
  const [diagnosticsCount, setDiagnosticsCount] = useState(0);

  useEffect(() => {
    setActsCount(loadStandardActCodes().length);
    setDiagnosticsCount(loadStandardDiagnosticCodes().length);
  }, []);

  return (
    <>
      <div className="mb-6">
        <Breadcrumbs homeHref={homeHref} items={[{ label: labels.title }]} />
        <p className="mt-4 max-w-3xl text-sm text-muted-foreground">{labels.subtitle}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <OverviewCard
          title={labels.actsCard.title}
          description={labels.actsCard.description}
          countLabel={labels.actsCard.countLabel.replace("{count}", String(actsCount))}
          linkLabel={labels.actsCard.link}
          to="/dashboard/acts/standard-codes"
          icon={<Stethoscope size={22} />}
        />
        <OverviewCard
          title={labels.diagnosticsCard.title}
          description={labels.diagnosticsCard.description}
          countLabel={labels.diagnosticsCard.countLabel.replace(
            "{count}",
            String(diagnosticsCount),
          )}
          linkLabel={labels.diagnosticsCard.link}
          to="/dashboard/acts/standard-diagnostics"
          icon={<ClipboardList size={22} />}
        />
      </div>
    </>
  );
}
